import React, { useEffect, useMemo, useState } from "react";
import { ArrowRight, SquareCheck } from "lucide-react";

import { Badge } from "../../components/ui/badge";
import { Button, StatefulButton } from "../../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import { cn } from "../../lib/utils";
import { paddleCheckout } from "./Paddle";
import {
  getCurrencySymbol,
  getPlanName,
  getPlanType,
} from "../../utils/helpers";
import { getPaddleBillingConfig } from "../../utils/paddleBilling";
import { createNotification } from "../../utils/notificationManager";
import {
  getBillingPeriodFromPlanName,
  getBillingPlanTypeFromName,
  getBillingTierLabel,
  getIntegerPriceLabel,
} from "./billingCatalog";
import { classicPlanTemplates, getClassicPlanId } from "./classicPlanCatalog";
import "./upgradePlan.css";

const PLAN_PROVIDER = {
  CLASSIC: "classic",
  BILLING: "billing",
};

function buildFeatures(groupLimit) {
  return [
    `Add up to ${groupLimit} Facebook groups`,
    "Unlimited members approval and lead collection",
    "Access to Google Sheet integration",
    "Integration with popular email marketing autoresponders",
    "Option to export data both in CSV and Excel format from dashboard.",
    "Create FB custom and lookalike audience",
    "VIP Facebook group for users",
    "Exclusive support through Chat/Zoom/Google Meet/Teams",
  ];
}

function getPackageDefaultPriceLabel(item) {
  const price = item?.price;
  return price != null && price !== "" ? getIntegerPriceLabel(`$${price}`) : "";
}

function findPlanTemplateById(planId) {
  const normalizedPlanId = String(planId || "");

  if (!normalizedPlanId) {
    return null;
  }

  for (const billingPeriod of Object.keys(classicPlanTemplates)) {
    for (const template of classicPlanTemplates[billingPeriod]) {
      const classicId = getClassicPlanId(template.envKey);

      if (
        [template.key, classicId]
          .filter(Boolean)
          .some((id) => String(id) === normalizedPlanId)
      ) {
        return template;
      }
    }
  }

  return null;
}

function getCurrentPlanPriceLabel(
  planId,
  unitPrice,
  currency,
  provider = PLAN_PROVIDER.CLASSIC
) {
  if (unitPrice != null && unitPrice !== "") {
    return getIntegerPriceLabel(
      `${currency ? getCurrencySymbol(currency) : "$"}${unitPrice}`
    );
  }

  if (provider === PLAN_PROVIDER.BILLING) {
    return "Price unavailable";
  }

  return getPackageDefaultPriceLabel(findPlanTemplateById(planId));
}

function buildClassicPlanPackages(billingPeriod) {
  return (classicPlanTemplates[billingPeriod] || []).flatMap((template) => {
    const classicId = getClassicPlanId(template.envKey);

    if (!classicId) {
      return [];
    }

    return [
      {
        provider: PLAN_PROVIDER.CLASSIC,
        id: classicId,
        name: template.name,
        tierLabel: template.tierLabel,
        price: template.price,
        groupLimit: template.groupLimit,
        features: buildFeatures(template.groupLimit),
        badge: template.badge,
      },
    ];
  });
}

export function buildBillingPlanPackages(billingPeriod, billingPlans = []) {
  return billingPlans
    .filter(
      (plan) => getBillingPeriodFromPlanName(plan?.plan_name) === billingPeriod
    )
    .map((plan, index) => ({
      provider: PLAN_PROVIDER.BILLING,
      id: plan.price_id,
      name: plan.plan_name,
      tierLabel: getBillingTierLabel(plan.plan_name),
      price: null,
      groupLimit: plan.allowed_groups,
      features: buildFeatures(plan.allowed_groups),
      badge:
        /pro/i.test(getBillingTierLabel(plan.plan_name)) || index === 1
          ? "Most Popular"
          : null,
      unavailableReason: null,
    }));
}

export function buildPlanPackages(
  billingPeriod,
  preferredProvider,
  billingPlans = []
) {
  return preferredProvider === PLAN_PROVIDER.BILLING
    ? buildBillingPlanPackages(billingPeriod, billingPlans)
    : buildClassicPlanPackages(billingPeriod);
}

function PlanNotice({ children, variant = "warning", action }) {
  return (
    <div className={cn("upgrade-plan-notice", `upgrade-plan-notice--${variant}`)} role={variant === "error" ? "alert" : "status"}>
      <div className="upgrade-plan-notice__message">{children}</div>
      {action}
    </div>
  );
}

function UpgradePlanCard({
  item,
  billingPeriod,
  isCurrent,
  isFeatured,
  onChoose,
  isDisabled,
  isPricePreviewLoading,
  actionLabel,
}) {
  const priceSuffix = billingPeriod === "yearly" ? "/ Year" : "/ Month";
  const priceLabel =
    isPricePreviewLoading && item.provider === PLAN_PROVIDER.BILLING
      ? "Loading price…"
      : item.previewPriceLabel ||
        getPackageDefaultPriceLabel(item) ||
        "Price unavailable";

  return (
    <article
      aria-label={`${item.tierLabel} plan`}
      className={cn(
        "upgrade-plan-card-wrap",
        isFeatured && "upgrade-plan-card-wrap--featured",
        isCurrent && "upgrade-plan-card-wrap--current"
      )}
    >
      {isFeatured && <div className="upgrade-plan-card__featured-label">Most popular</div>}
      <div className="upgrade-plan-card">
        <div className="upgrade-plan-card__top">
          <div className="upgrade-plan-card__topline">
            <Badge className="upgrade-plan-card__tier" variant="secondary">
              {item.tierLabel}
            </Badge>
          </div>
          <div
            className={cn(
              "upgrade-plan-card__price-row",
              priceLabel === "Price unavailable" && "upgrade-plan-card__price-row--unavailable"
            )}
          >
            <span className="upgrade-plan-card__price">{priceLabel}</span>
            <span className="upgrade-plan-card__price-suffix">{priceSuffix}</span>
          </div>
        </div>

        <ul className="upgrade-plan-card__features">
          {item.features.map((feature) => (
            <li className="upgrade-plan-card__feature" key={feature}>
              <SquareCheck
                aria-hidden="true"
                className="upgrade-plan-card__feature-icon"
                size={18}
                strokeWidth={2.2}
              />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        <Button
          className="upgrade-plan-card__cta"
          disabled={isCurrent || isDisabled}
          onClick={onChoose}
          type="button"
          variant={isCurrent || isDisabled ? "secondary" : "primary"}
        >
          {isCurrent
            ? "Current plan"
            : isDisabled
              ? "Unavailable"
              : actionLabel || "Choose plan"}
        </Button>

        {item.unavailableReason && (
          <p className="upgrade-plan-card__unavailable">{item.unavailableReason}</p>
        )}
      </div>
    </article>
  );
}

function UpgradePlanModal({
  modal,
  setModal,
  planStatus,
  planId,
  currentPlanUnitPrice,
  currentPlanCurrency,
  currentPlanName,
  userMail,
  isBilling,
  billingPlans,
  billingPlansLoading,
  billingPlansError,
  billingPriceLabels,
  billingPricePreviewLoading,
  retryBillingPlans,
  billingActions,
  initiatePlanUpgrade,
  initiateBillingCheckout,
  billingPlanChangeLoading,
  billingMutationError,
  confirmBillingPlanChange,
}) {
  const [billingPeriod, setBillingPeriod] = useState("yearly");
  const [selectedBillingPlan, setSelectedBillingPlan] = useState(null);

  const hasActivePlan = Boolean(
    planStatus && planStatus !== "canceled" && planStatus !== "inactive"
  );
  const subscriptionActions = billingActions || {};
  const billingConfig = getPaddleBillingConfig();
  const preferredProvider =
    !hasActivePlan || isBilling ? PLAN_PROVIDER.BILLING : PLAN_PROVIDER.CLASSIC;
  const availableBillingPeriods = useMemo(
    () =>
      [
        ...new Set(
          (billingPlans || [])
            .map((plan) => getBillingPeriodFromPlanName(plan?.plan_name))
            .filter(Boolean)
        ),
      ],
    [billingPlans]
  );
  const packages = useMemo(
    () => buildPlanPackages(billingPeriod, preferredProvider, billingPlans),
    [billingPeriod, billingPlans, preferredProvider]
  );
  const packagesWithPreviewPrices = useMemo(
    () =>
      packages.map((item) => ({
        ...item,
        previewPriceLabel: billingPriceLabels?.[item.id] || null,
      })),
    [billingPriceLabels, packages]
  );
  const showBillingConfigError =
    !hasActivePlan &&
    preferredProvider === PLAN_PROVIDER.BILLING &&
    !billingConfig.isBillingConfigured;
  const showBillingCatalogLoading =
    preferredProvider === PLAN_PROVIDER.BILLING && billingPlansLoading;
  const showBillingCatalogError =
    preferredProvider === PLAN_PROVIDER.BILLING && Boolean(billingPlansError);
  const showBillingCatalogEmpty =
    preferredProvider === PLAN_PROVIDER.BILLING &&
    !billingPlansLoading &&
    !billingPlansError &&
    availableBillingPeriods.length === 0;
  const showBillingPreviewStep =
    Boolean(selectedBillingPlan) &&
    !showBillingCatalogLoading &&
    !showBillingCatalogError &&
    !showBillingCatalogEmpty;

  useEffect(() => {
    if (!planId) {
      return;
    }

    const planType = currentPlanName
      ? getBillingPeriodFromPlanName(currentPlanName)
      : getPlanType(planId);
    setBillingPeriod(planType.includes("month") ? "monthly" : "yearly");
  }, [currentPlanName, planId]);

  useEffect(() => {
    if (!modal?.upgradeModal) {
      setSelectedBillingPlan(null);
    }
  }, [modal?.upgradeModal]);

  useEffect(() => {
    if (
      preferredProvider !== PLAN_PROVIDER.BILLING ||
      !availableBillingPeriods.length
    ) {
      return;
    }

    const currentPlanPeriod = getBillingPeriodFromPlanName(currentPlanName);
    const preferredPeriod =
      currentPlanPeriod && availableBillingPeriods.includes(currentPlanPeriod)
        ? currentPlanPeriod
        : availableBillingPeriods.includes("yearly")
          ? "yearly"
          : availableBillingPeriods[0];

    if (!availableBillingPeriods.includes(billingPeriod)) {
      setBillingPeriod(preferredPeriod);
    }
  }, [
    availableBillingPeriods,
    billingPeriod,
    currentPlanName,
    preferredProvider,
  ]);

  const closeModal = () => {
    setSelectedBillingPlan(null);
    setModal((current) => ({ ...current, upgradeModal: false }));
  };

  const openCheckout = (id, name) => {
    paddleCheckout(id, name, userMail);
  };

  const handleChoosePlan = (item) => {
    if (item.unavailableReason) {
      createNotification("warning", item.unavailableReason, 4000);
      return;
    }

    if (String(item.id) === String(planId) && planStatus !== "canceled") {
      createNotification("warning", "You already have this plan", 2000);
      return;
    }

    if (
      item.provider === PLAN_PROVIDER.BILLING &&
      !billingConfig.isBillingConfigured
    ) {
      createNotification("warning", billingConfig.configError, 4000);
      return;
    }

    if (
      item.provider === PLAN_PROVIDER.BILLING &&
      !(billingPlans || []).some(
        (plan) => String(plan?.price_id) === String(item.id)
      )
    ) {
      createNotification(
        "warning",
        "This Billing plan is no longer available. Reload the catalog and try again.",
        4000
      );
      return;
    }

    if (!hasActivePlan && item.provider === PLAN_PROVIDER.CLASSIC) {
      openCheckout(item.id, item.name);
      closeModal();
      return;
    }

    if (!hasActivePlan && item.provider === PLAN_PROVIDER.BILLING) {
      initiateBillingCheckout({
        price_id: item.id,
        email: userMail,
      });
      closeModal();
      return;
    }

    if (item.provider === PLAN_PROVIDER.CLASSIC && !isBilling) {
      initiatePlanUpgrade({ plan_id: item.id });
      closeModal();
      return;
    }

    if (item.provider === PLAN_PROVIDER.BILLING && isBilling) {
      setSelectedBillingPlan(item);
      return;
    }

    createNotification("warning", "Unable to change the selected plan.", 3000);
  };

  const isCurrentPlan = (item) =>
    String(item.id) === String(planId) && planStatus !== "canceled";
  const displayedCurrentPlanName = currentPlanName || getPlanName(planId);
  const currentPlanType = currentPlanName
    ? getBillingPlanTypeFromName(currentPlanName)
    : getPlanType(planId);
  const currentPlanPrice = getCurrentPlanPriceLabel(
    planId,
    currentPlanUnitPrice,
    currentPlanCurrency,
    isBilling ? PLAN_PROVIDER.BILLING : PLAN_PROVIDER.CLASSIC
  );
  const targetPlanName = selectedBillingPlan?.name || "Unknown";
  const targetPlanType = getBillingPlanTypeFromName(targetPlanName);
  const targetPlanPrice = billingPricePreviewLoading
    ? "Loading price…"
    : selectedBillingPlan?.previewPriceLabel || "Price unavailable";

  return (
    <Dialog
      onOpenChange={(open) => {
        if (!open) closeModal();
      }}
      open={Boolean(modal?.upgradeModal)}
    >
      <DialogContent className="upgrade-plan-dialog" showCloseButton>
        {!showBillingPreviewStep && (
          <DialogHeader className="upgrade-plan-dialog__header">
            <DialogTitle>Choose the plan that fits your growth</DialogTitle>
            <DialogDescription>
              {isBilling && hasActivePlan
                ? "Choose a Billing plan to continue in Paddle."
                : "Simple pricing. Powerful results. Cancel anytime."}
            </DialogDescription>
          </DialogHeader>
        )}

        <div className="upgrade-plan-dialog__body">
          {showBillingConfigError && (
            <PlanNotice variant="warning">
              Paddle Billing is not configured correctly. {billingConfig.configError}
            </PlanNotice>
          )}

          {showBillingCatalogError && (
            <PlanNotice
              action={
                <Button
                  disabled={billingPlansLoading}
                  onClick={retryBillingPlans}
                  size="sm"
                  type="button"
                  variant="secondary"
                >
                  Retry
                </Button>
              }
              variant="error"
            >
              {billingPlansError}
            </PlanNotice>
          )}

          {subscriptionActions.can_change_plan === false &&
            subscriptionActions.blocked_reason && (
              <PlanNotice variant="warning">
                {subscriptionActions.blocked_reason}
              </PlanNotice>
            )}

          {showBillingCatalogEmpty && (
            <PlanNotice variant="info">No Billing plans are available right now.</PlanNotice>
          )}

          {showBillingPreviewStep ? (
            <section aria-labelledby="upgrade-plan-review-title" className="upgrade-plan-review">
              <div className="upgrade-plan-review__heading">
                <h2 id="upgrade-plan-review-title">Review your plan change</h2>
                <p>Check the current and selected plan before continuing.</p>
              </div>

              {billingMutationError && (
                <PlanNotice variant="error">{billingMutationError}</PlanNotice>
              )}

              <div className="upgrade-plan-review__plans">
                <div className="upgrade-plan-review__plan">
                  <span className="upgrade-plan-review__label">Current plan</span>
                  <strong>{displayedCurrentPlanName}</strong>
                  <span>
                    {currentPlanPrice} {currentPlanType || "Current interval"}
                  </span>
                </div>
                <ArrowRight
                  aria-hidden="true"
                  className="upgrade-plan-review__arrow"
                  size={24}
                  strokeWidth={2}
                />
                <div className="upgrade-plan-review__plan upgrade-plan-review__plan--new">
                  <span className="upgrade-plan-review__label">New plan</span>
                  <strong>{targetPlanName}</strong>
                  <span>
                    {targetPlanPrice} {targetPlanType || ""}
                  </span>
                </div>
              </div>

              <div className="upgrade-plan-review__actions">
                <Button
                  disabled={billingPlanChangeLoading}
                  onClick={() => setSelectedBillingPlan(null)}
                  type="button"
                  variant="secondary"
                >
                  Back
                </Button>
                <StatefulButton
                  disabled={!selectedBillingPlan}
                  loadingText="Applying…"
                  onClick={() =>
                    confirmBillingPlanChange({
                      new_price_id: selectedBillingPlan?.id,
                      proration_billing_mode: "prorated_immediately",
                      on_payment_failure: "prevent_change",
                    })
                  }
                  state={billingPlanChangeLoading ? "loading" : "idle"}
                  type="button"
                  variant="primary"
                >
                  Confirm change
                </StatefulButton>
              </div>
            </section>
          ) : (
            <>
              {!showBillingCatalogLoading &&
                !showBillingCatalogError &&
                !showBillingCatalogEmpty && (
                  <div className="upgrade-plan-period-row">
                    <div className="upgrade-plan-period-row__period-control">
                      <div className="upgrade-plan-period-tabs-shell">
                        <span className="upgrade-plan-period-tabs__discount">
                          60% off!
                        </span>
                        <Tabs
                          className="upgrade-plan-period-tabs"
                        onValueChange={setBillingPeriod}
                        value={billingPeriod}
                        variant="segment"
                      >
                        <TabsList
                          aria-label="Billing period"
                          className="upgrade-plan-period-tabs__list"
                        >
                          {(preferredProvider === PLAN_PROVIDER.CLASSIC ||
                            availableBillingPeriods.includes("yearly")) && (
                            <TabsTrigger
                              className="upgrade-plan-period-tabs__trigger"
                              value="yearly"
                            >
                              Yearly
                            </TabsTrigger>
                          )}
                          {(preferredProvider === PLAN_PROVIDER.CLASSIC ||
                            availableBillingPeriods.includes("monthly")) && (
                            <TabsTrigger
                              className="upgrade-plan-period-tabs__trigger"
                              value="monthly"
                            >
                              <span>Monthly</span>
                            </TabsTrigger>
                          )}
                        </TabsList>
                        </Tabs>
                      </div>
                    </div>
                  </div>
                )}

              {!showBillingCatalogLoading &&
                !showBillingCatalogError &&
                !showBillingCatalogEmpty && (
                  <div className="upgrade-plan-grid">
                    {packagesWithPreviewPrices.map((item) => (
                      <UpgradePlanCard
                        actionLabel={
                          item.provider === PLAN_PROVIDER.BILLING &&
                          isBilling &&
                          hasActivePlan
                            ? "Review change"
                            : undefined
                        }
                        billingPeriod={billingPeriod}
                        isCurrent={isCurrentPlan(item)}
                        isDisabled={
                          Boolean(item.unavailableReason) ||
                          (item.provider === PLAN_PROVIDER.BILLING &&
                            isBilling &&
                            hasActivePlan &&
                            subscriptionActions.can_change_plan === false)
                        }
                        isFeatured={item.badge === "Most Popular"}
                        isPricePreviewLoading={billingPricePreviewLoading}
                        item={item}
                        key={String(item.id)}
                        onChoose={() => handleChoosePlan(item)}
                      />
                    ))}
                  </div>
                )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default UpgradePlanModal;
